import { normalizeHourForApi } from './UpdateCustomAccountsDownloadConfig';
import { flowTypeFromPreviewType } from './flow_preview_constants';
import { isExcelImportKey, isJsonImportKey } from './preview_map_utils';

export const PREVIEW_SOURCE_PLUGGY = 'generated_pluggy';
export const PREVIEW_SOURCE_JSON = 'generated_json';
export const PREVIEW_SOURCE_EXCEL = 'generated_excel';

/** Django FlowPreliminaryTransaction: DecimalField(max_digits=12, decimal_places=2) */
const MAX_MONEY_12_2 = 9999999999.99;

/**
 * Round to 2 decimal places and clamp to DB Decimal(12,2) range (avoids float noise & validation errors).
 */
export function toDecimal12_2(value) {
  const x = Number(value);
  if (!Number.isFinite(x)) return 0;
  const cents = Math.round(x * 100);
  let rounded = cents / 100;
  if (rounded > MAX_MONEY_12_2) return MAX_MONEY_12_2;
  if (rounded < -MAX_MONEY_12_2) return -MAX_MONEY_12_2;
  return rounded;
}

/** exchange_rate: DecimalField(max_digits=18, decimal_places=6) */
function toExchangeRate6dp(value) {
  const x = Number(value);
  if (!Number.isFinite(x) || x === 0) return null;
  return Math.round(x * 1e6) / 1e6;
}

export function resolvePreviewSource(accountId) {
  const id = String(accountId ?? '');
  if (isJsonImportKey(id)) return PREVIEW_SOURCE_JSON;
  if (isExcelImportKey(id)) return PREVIEW_SOURCE_EXCEL;
  return PREVIEW_SOURCE_PLUGGY;
}

/**
 * One preview row → API body for FlowPreliminary (matches FlowPreliminaryTransactionSerializer write fields).
 * Expects baked preview: `date` = YYYY-MM-DD, `hour` = HH:MM:SS (set at fetch/load).
 */
export function previewRowToPreliminaryPayload(
  row,
  {
    flowAccountName,
    source,
    defaultCategL0,
    exchangeRates,
    defaultCurrency,
  }
) {
  const dateStr = String(row.date || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    throw new Error('Invalid date (reload preview with Fetch/Load)');
  }
  const hourStr = normalizeHourForApi(row.hour, '00:00:00');

  const ft =
    row.flow_type === 'In' || row.flow_type === 'Out'
      ? row.flow_type
      : flowTypeFromPreviewType(row.raw_type);
  if (ft !== 'In' && ft !== 'Out') {
    throw new Error(`Unknown flow_type (raw: ${row.raw_type || 'empty'})`);
  }

  const flow_currency = (row.flow_currency || 'BRL').toString().trim() || 'BRL';
  const amountRaw = Math.abs(Number(row.flow_value) || 0);
  const flow_value = toDecimal12_2(amountRaw);
  const exchangeRateRaw = Number(exchangeRates[flow_currency] ?? 0) || 0;
  const exchange_rate = toExchangeRate6dp(exchangeRateRaw);
  const rateForCalc = exchange_rate ?? 0;
  const exchangeId = `${flow_currency}_${defaultCurrency || 'BRL'}`;
  const baseValue = flow_value * rateForCalc;
  const std_value = toDecimal12_2(ft === 'Out' ? -baseValue : baseValue);

  const hasCC =
    row.extras_cardnumber ||
    row.payee_mcc ||
    row.purchase_date ||
    (row.total_installments != null && row.total_installments !== '') ||
    (row.installment_number != null && row.installment_number !== '');

  let extras_info = null;
  if (hasCC) {
    extras_info = {
      creditCard: {
        payeeMCC: row.payee_mcc || 'none',
        purchaseDate: row.purchase_date || 'none',
        totalInstallments: row.total_installments ?? null,
        installmentNumber: row.installment_number ?? null,
      },
    };
  }

  const categ_l0 = (defaultCategL0 || '').trim();
  if (!categ_l0) {
    throw new Error('default categ_l0 is missing (profile)');
  }

  const desc = (row.desc || '').trim().slice(0, 255);
  if (!desc) {
    throw new Error('desc is required');
  }

  const extRaw = row.external_id != null ? String(row.external_id).trim() : '';
  const external_id = extRaw || null;

  return {
    date: dateStr,
    hour: hourStr,
    desc,
    categ_l0,
    categ_l1: row.categ_l1?.trim?.() || null,
    categ_l2: null,
    reference: null,
    flow_type: ft,
    flow_value,
    flow_currency,
    std_value,
    flow_account: (flowAccountName || '').trim().slice(0, 100) || null,
    extras_description: row.extras_description?.trim?.() || null,
    extras_category: row.categ_l1?.trim?.() || null,
    extras_id: row.extras_id?.trim?.() || null,
    extras_cardnumber: row.extras_cardnumber?.trim?.() || null,
    extras_info,
    exchange_id: exchangeId,
    exchange_rate,
    status: row.status?.trim?.() ?? '',
    source,
    external_id,
  };
}

/**
 * Walk all accounts in preview state; build preliminary payloads. Skips accounts with `error`.
 * Optional `ctx.trimMergeStart` / `ctx.trimMergeEnd` (YYYY-MM-DD): when both set, rows outside
 * [start, end] inclusive (by `row.date`) are skipped. `row.hour` is ignored for filtering.
 * @returns {{ payloads: object[], errors: string[] }}
 */
export function buildPreliminaryPayloadsFromPreviewMap(openfinanceData, ctx) {
  const payloads = [];
  const errors = [];

  const trimStart = (ctx.trimMergeStart || '').trim();
  const trimEnd = (ctx.trimMergeEnd || '').trim();
  const trimActive = trimStart !== '' && trimEnd !== '';

  Object.entries(openfinanceData || {}).forEach(([accountId, data]) => {
    if (!data || data.error) return;
    const txs = data.transactions;
    if (!txs?.length) return;
    const source = resolvePreviewSource(String(accountId));
    const flowAccountName = data.account?.account_name || '';

    txs.forEach((row, idx) => {
      if (trimActive) {
        const d = String(row.date || '').trim();
        if (/^\d{4}-\d{2}-\d{2}$/.test(d) && (d < trimStart || d > trimEnd)) {
          return;
        }
      }
      try {
        payloads.push(
          previewRowToPreliminaryPayload(row, {
            ...ctx,
            flowAccountName,
            source,
          })
        );
      } catch (e) {
        errors.push(`${flowAccountName || accountId} row ${idx + 1}: ${e.message}`);
      }
    });
  });

  return { payloads, errors };
}
